/* @flow */

import type { History } from "react-router";

import React, { useContext } from "react";
import { Route, Switch } from "react-router";
import Redirect from "@crossroads/react-router-redirect";
import CmsPageView from "components/CmsPageView";
import {
  RouteData, CustomerData, QuoteData, OrderData, HomeData, BrandData, SearchData,
  WishlistData, CmsPageData, PopularData, AdventCalendarData, AllProductsData,
  CmsBlockData,
} from "data";
import { useTranslate } from "@awardit/react-use-translate";
import { PAGE_SIZE } from "effects/route";
import { useData } from "crustate/react";
import App from "components/App";
import LoadingView from "components/LoadingView";
import SearchView from "components/SearchView";
import { ProductListViewWrapper, ProductListView, HintProductListView, AdventCalendarView } from "@crossroads/shop-views";
import ProductView, { HintProductView } from "components/ProductView";
import HomeView from "components/HomeView";
import AccountView from "components/AccountView";
import SuccessView from "components/SuccessView";
import CheckoutView from "components/CheckoutView";
import BuyAndTransferView from "components/BuyAndTransferView";
import NotFoundView from "components/NotFoundView";
import CountrySelection from "components/CountrySelection";
import ProductCard from "components/ProductCard";
import ProductList from "components/ProductList";
import CategorySection from "components/CategorySection";
import { parseParams } from "helpers/location-search-string";
import { useUi } from "helpers/ui";
import { locationWithDefaults } from "helpers/use-update-product-list";
import { StoreInfoContext } from "entrypoint/shared";
import useAdventCalendarEnabled from "helpers/use-advent-calendar-enabled";
import { useNotification } from "helpers/use-notification";

type RoutesProps = {
  location: Location,
  history: History,
};

type RouteViewProps = {
  location: Location,
  history: {
    replace: ({}) => void,
  },
};

const RouteView = ({ location, history }: RouteViewProps) => {
  const t = useTranslate();
  const data = useData(RouteData);
  const homeData = useData(HomeData);
  const onAdventCalendarCategoryPath = location.pathname.includes(t("ADVENT_CALENDAR.CATEGORY_URL"));
  const { info: { locale } } = useContext(StoreInfoContext);
  const gitCardsRoute = "/gift-cards-vouchers";
  const { heightToUse } = useNotification();

  if (data.state === "LOADING") {
    if (data.hint) {
      switch (data.hint.type) {
        case "product":
          return <HintProductView product={data.hint.product} />;
        case "category":
          return (
            <HintProductListView
              title={data.hint.category.name}
              StoreInfoContext={StoreInfoContext}
              loadingText={t("CATEGORY.PRODUCTS_IN_CATEGORY_TITLE_EMPTY")}
            />
          );
        case "cms_page":
          return <CmsPageView cmsPage={data.hint.cmsPage} />;
        default:
      }
    }

    return <LoadingView />;
  }

  if (!data.route) {
    return <NotFoundView />;
  }

  switch (data.route.type) {
    case "product":
      return <ProductView product={data.route.product} />;
    case "category":
      if (location.pathname === gitCardsRoute && locale === "en_GB") {
        return <CountrySelection locale={locale} />;
      }

      const title = onAdventCalendarCategoryPath &&
      data.route.category.heroHeading !== null &&
      data.route.category.heroHeading !== undefined ?
        data.route.category.heroHeading : data.route.category.name;
      return (
        <ProductListView
          hasFilters
          hasPagination
          hasPoints
          hasTitleParts
          adventCalendarBack
          hasAdventCalendarHero
          hasTotalCount
          variant="category"
          history={history}
          category={data.route.category}
          home={homeData.state === "LOADED" ? homeData.data : {}}
          productList={data.route.category.products}
          title={title}
          isUpdating={data.state === "UPDATING"}
          pageSize={PAGE_SIZE}
          StoreInfoContext={StoreInfoContext}
          uiContext={useUi}
          currentBreadcrumb={data.route.category.name}
          productsLink={location.pathname}
          ProductCardComponent={ProductCard}
          sortButtonSide="right"
          pointsPriceId="sas_extrapoints"
          loadingText={t("CATEGORY.PRODUCTS_IN_CATEGORY_TITLE_EMPTY")}
          filterbarOffsetClass={heightToUse() !== "none" ? `${heightToUse()}Offset` : ""}
          {...(heightToUse() !== "none" && { filterbarOffsetClass: `${heightToUse()}Offset` })}
          CampaignCardData={CmsBlockData}
          afterContent={<CategorySection useWrapper />}
          onAdventCalendarPath={t("ADVENT_CALENDAR.CATEGORY_URL")}
        />
      );
    case "cms_page":
      return <CmsPageView cmsPage={data.route.cmsPage} />;
    case "redirect":
      return <Redirect to={data.route.url} />;
    default:
      return null;
  }
};

const Routes = ({ location, history }: RoutesProps) => {
  const t = useTranslate();
  const customerData = useData(CustomerData);
  const quoteData = useData(QuoteData);
  const adventCalendarEnabled = useAdventCalendarEnabled();
  const { heightToUse } = useNotification();

  return (
    <Switch>
      <App location={location} history={history}>
        <Switch>
          <Route
            exact path="/" render={() => (
              <HomeData.Provider>
                <HomeView />
              </HomeData.Provider>
            )} />

          <Route path="/account/buy-and-transfer-points" component={BuyAndTransferView} />

          <Route
            path="/account"
            render={({ history }) => {
              if (customerData.state === "ERROR") {
                history.push("/");
                return null;
              }

              if (!customerData.data || quoteData.data === null || quoteData.state === "LOADING") {
                history.push("/");
                return null;
              }

              return (
                <WishlistData.Provider>
                  <Switch>
                    <Route
                      exact
                      path="/account"
                      render={() =>
                        <AccountView customer={customerData.data} quote={quoteData.data} />
                      } />
                    <Route exact path="/account/buy-and-transfer-points" component={BuyAndTransferView} />
                  </Switch>
                </WishlistData.Provider>
              );
            }}
          />

          <Route
            path="/checkout/success"
            render={({ history }) => (
              <OrderData.Provider>
                <SuccessView history={history} />
              </OrderData.Provider>
            )} />

          <Route
            exact
            path="/checkout/:step?"
            render={props => (
              <CmsPageData.Provider url="terms">
                <CheckoutView {...props} />
              </CmsPageData.Provider>
            )} />

          <Route
            path="/brand/:brand"
            render={({ match, location: { search } }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <BrandData.Provider
                    location={locationWithDefaults(location)}
                    name={match.params.brand}
                    brand={decodeURIComponent(match.params.brand)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        hasPoints
                        hasTitleParts
                        variant="brands"
                        data={BrandData}
                        pageSize={PAGE_SIZE}
                        StoreInfoContext={StoreInfoContext}
                        uiContext={useUi}
                        ProductCardComponent={ProductCard}
                        sortButtonSide="right"
                        pointsPriceId="sas_extrapoints"
                        loadingText={t("CATEGORY.PRODUCTS_IN_CATEGORY_TITLE_EMPTY")}
                        afterContent={<CategorySection useWrapper />}
                        {...(heightToUse() !== "none" && { filterbarOffsetClass: `${heightToUse()}Offset` })}
                        CampaignCardData={CmsBlockData}
                      />
                    </CmsBlockData.Provider>
                  </BrandData.Provider>
                </HomeData.Provider>
              );
            }} />

          <Route
            exact path="/popular-products"
            render={({ location: { search } }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <PopularData.Provider
                    location={locationWithDefaults(location)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        hasPoints
                        hasTitleParts
                        variant="popular"
                        data={PopularData}
                        pageSize={PAGE_SIZE}
                        StoreInfoContext={StoreInfoContext}
                        uiContext={useUi}
                        title={t("POPULAR.POPULAR_PRODUCTS")}
                        ProductCardComponent={ProductCard}
                        sortButtonSide="right"
                        pointsPriceId="sas_extrapoints"
                        {...(heightToUse() !== "none" && { filterbarOffsetClass: `${heightToUse()}Offset` })}
                        CampaignCardData={CmsBlockData}
                        afterContent={<CategorySection useWrapper />}
                      />
                    </CmsBlockData.Provider>
                  </PopularData.Provider>
                </HomeData.Provider>
              );
            }} />

          <Route
            exact path="/all-products"
            render={({ location: { search } }) => {
              const { page } = parseParams(search);

              return (
                <HomeData.Provider>
                  <AllProductsData.Provider
                    location={locationWithDefaults(location)}
                    page={page}
                  >
                    <CmsBlockData.Provider identifier="campaign-card">
                      <ProductListViewWrapper
                        hasPoints
                        hasTitleParts
                        variant="allproducts"
                        data={AllProductsData}
                        pageSize={PAGE_SIZE}
                        StoreInfoContext={StoreInfoContext}
                        uiContext={useUi}
                        title={t("ALL_PRODUCTS.TITLE")}
                        ProductCardComponent={ProductCard}
                        sortButtonSide="right"
                        pointsPriceId="sas_extrapoints"
                        loadingText={t("ALL_PRODUCTS.TITLE_PREFIX_LOADING")}
                        afterContent={<CategorySection useWrapper />}
                        {...(heightToUse() !== "none" && { filterbarOffsetClass: `${heightToUse()}Offset` })}
                        CampaignCardData={CmsBlockData}
                      />
                    </CmsBlockData.Provider>
                  </AllProductsData.Provider>
                </HomeData.Provider>
              );
            }} />

          {adventCalendarEnabled &&
            <Route
              exact
              path="/advent-calendar"
              render={() => {
                return (
                  <HomeData.Provider>
                    <AdventCalendarView
                      calendarData={AdventCalendarData}
                      homeData={HomeData}
                      LoadingView={LoadingView}
                      ProductListComponent={ProductList}
                      buttonVariantToday="secondary"
                      buttonVariantDoors="primary"
                    />
                  </HomeData.Provider>
                );
              }} />
          }

          <Route
            path="/search/:query"
            render={({ match, location: { search } }) => {
              const { page } = parseParams(search);

              const query = decodeURIComponent(match.params.query);

              return (
                <SearchData.Provider
                  location={locationWithDefaults(location)}
                  page={page}
                  query={query}
                  name={`search_${match.params.query}`}
                >
                  <SearchView query={query} />
                </SearchData.Provider>
              );
            }}
          />

          <Route
            path="/(.+)"
            render={({ location: { pathname, search, state }, history }) => {
              const { page } = parseParams(search);

              return (
                <RouteData.Provider
                  location={locationWithDefaults(location)}
                  url={pathname}
                  name={`route_${pathname}`}
                  page={page}
                  hint={state && state.hint}
                >
                  <HomeData.Provider>
                    <CmsBlockData.Provider identifier="campaign-card">
                      <RouteView location={location} history={history} />
                    </CmsBlockData.Provider>
                  </HomeData.Provider>
                </RouteData.Provider>
              );
            }} />
        </Switch>
      </App>
    </Switch>
  );
};

const AppRoutes = () => <Route component={Routes} />;

export default AppRoutes;
